export const AGENT_HUB_TRAVELLER_RESPONSE_TYPE = 'agent_hub_traveller'

interface FlightService {
  type: string
  included: boolean
  description: string | null
  quantity?: number | null
  weight_kg?: number | null
}

interface FlightSegment {
  departure_airport: string | null
  departure_name: string | null
  arrival_airport: string | null
  arrival_name: string | null
  departure_date: string | null
  departure_time: string | null
  arrival_date: string | null
  arrival_time: string | null
  departure_terminal: string | null
  arrival_terminal: string | null
  airline_name: string | null
  airline_code: string | null
  airline_logo_url: string | null
  flight_number: string | null
  aircraft: string | null
  duration_minutes: number | null
}

export interface AgentHubTravellerApiItem {
  id: string
  type: string
  status: string
  booking_number: string | null
  title: string | null
  offer_id?: string | null
  location?: string | null
  image_id?: string | null
  package_name?: string | null
  flight_details?: {
    legs: Array<{
      airline_reference: string | null
      fare_name: string | null
      duration_minutes: number | null
      services: Array<FlightService>
      segments: Array<FlightSegment>
      travellers: Array<{ first_name: string | null, last_name: string | null, title: string | null, services: Array<FlightService> }>
    }>
  }
  tour_details?: {
    start_location: string | null
    end_location: string | null
    operated_by: string | null
    duration: number | null
    countries: Array<string>
    places: number | null
    package_name: string | null
    rooms: Array<{ room_number: number, room_type: string | null, adults: number | null, children: number | null }>
    meeting_instructions: Array<string>
    joining_instructions: Array<string>
    itinerary_changes: Array<string>
  }
  room?: {
    name: string | null
    image_id: string | null
  } | null
  address?: string | null
  inclusions?: Array<string> | null
  fine_print?: Array<string> | null
  guests?: {
    adults: number | null
    children: number | null
    infants: number | null
  } | null
  dates: {
    start_date: string | Date | null
    end_date: string | Date | null
    expiration_date?: string | Date | null
  }
  travellers?: Array<{
    first_name: string | null
    last_name: string | null
    title?: string | null
    room_type?: string | null
    room_number?: number | null
    is_primary_contact?: boolean
  }>
  room_allocations?: Array<{
    name: string | null
    room_index: number
    cancellation?: {
      deadline: string | Date | null
      timezone: string | null
      type: 'free' | 'partially_refundable'
    } | null
    travellers: Array<{
      first_name: string | null
      last_name: string | null
    }>
  }>
  itinerary?: Array<{
    day: number
    title: string | null
    details: Array<string>
  }>
  airline_logo_url?: string | null
  trip_type?: 'return' | 'one_way' | null
  property?: {
    id: string | null
    name: string | null
  }
}

export interface AgentHubTravellerApiContact {
  name?: string | null
  agency_name?: string | null
  email?: string | null
  guidance?: string
}

export interface AgentHubTravellerApiOrder {
  response_type: typeof AGENT_HUB_TRAVELLER_RESPONSE_TYPE
  id: string
  number: string
  brand: string
  status: App.OrderStatus | string
  display_status: string
  created_at: string
  updated_at: string
  items: Array<AgentHubTravellerApiItem>
  agent_contact: AgentHubTravellerApiContact
}

const emptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined

const dateString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString()
  }
  return undefined
}

export function isAgentHubTravellerApiResponse(order: unknown): order is AgentHubTravellerApiOrder {
  return Boolean(
    order &&
    typeof order === 'object' &&
    (order as AgentHubTravellerApiOrder).response_type === AGENT_HUB_TRAVELLER_RESPONSE_TYPE,
  )
}

const mapFlightService = (service: FlightService): App.AgentHubTravellerFlightService => ({
  type: service.type,
  included: service.included,
  description: emptyString(service.description),
  quantity: service.quantity ?? undefined,
  weightKg: service.weight_kg ?? undefined,
})

function mapItem(item: AgentHubTravellerApiItem): App.AgentHubTravellerItem {
  return {
    id: String(item.id),
    type: item.type,
    status: item.status,
    bookingNumber: emptyString(item.booking_number),
    title: emptyString(item.title),
    offerId: emptyString(item.offer_id),
    location: emptyString(item.location),
    imageId: emptyString(item.image_id),
    packageName: emptyString(item.package_name),
    flightDetails: item.flight_details ? {
      legs: item.flight_details.legs.map(leg => ({
        airlineReference: emptyString(leg.airline_reference),
        fareName: emptyString(leg.fare_name),
        durationMinutes: leg.duration_minutes ?? undefined,
        services: leg.services.map(mapFlightService),
        segments: leg.segments.map(segment => ({
          departureAirport: emptyString(segment.departure_airport),
          departureName: emptyString(segment.departure_name),
          arrivalAirport: emptyString(segment.arrival_airport),
          arrivalName: emptyString(segment.arrival_name),
          departureDate: emptyString(segment.departure_date),
          departureTime: emptyString(segment.departure_time),
          arrivalDate: emptyString(segment.arrival_date),
          arrivalTime: emptyString(segment.arrival_time),
          departureTerminal: emptyString(segment.departure_terminal),
          arrivalTerminal: emptyString(segment.arrival_terminal),
          airlineName: emptyString(segment.airline_name),
          airlineCode: emptyString(segment.airline_code),
          airlineLogoUrl: emptyString(segment.airline_logo_url),
          flightNumber: emptyString(segment.flight_number),
          aircraft: emptyString(segment.aircraft),
          durationMinutes: segment.duration_minutes ?? undefined,
        })),
        travellers: leg.travellers.map(traveller => ({
          firstName: emptyString(traveller.first_name),
          lastName: emptyString(traveller.last_name),
          title: emptyString(traveller.title),
          services: traveller.services.map(mapFlightService),
        })),
      })),
    } : undefined,
    tourDetails: item.tour_details ? {
      startLocation: emptyString(item.tour_details.start_location),
      endLocation: emptyString(item.tour_details.end_location),
      operatedBy: emptyString(item.tour_details.operated_by),
      duration: item.tour_details.duration ?? undefined,
      countries: item.tour_details.countries,
      places: item.tour_details.places ?? undefined,
      packageName: emptyString(item.tour_details.package_name),
      rooms: item.tour_details.rooms.map(room => ({
        roomNumber: room.room_number,
        roomType: emptyString(room.room_type),
        adults: room.adults ?? undefined,
        children: room.children ?? undefined,
      })),
      meetingInstructions: item.tour_details.meeting_instructions.join('\n'),
      joiningInstructions: item.tour_details.joining_instructions.join('\n'),
      itineraryChanges: item.tour_details.itinerary_changes.join('\n'),
    } : undefined,
    room: item.room ? {
      name: emptyString(item.room.name),
      imageId: emptyString(item.room.image_id),
    } : undefined,
    address: emptyString(item.address),
    inclusions: (item.inclusions ?? []).map(emptyString).filter((value): value is string => Boolean(value)),
    finePrint: (item.fine_print ?? []).map(emptyString).filter((value): value is string => Boolean(value)),
    guests: item.guests ? {
      adults: item.guests.adults ?? undefined,
      children: item.guests.children ?? undefined,
      infants: item.guests.infants ?? undefined,
    } : undefined,
    startDate: dateString(item.dates?.start_date),
    expirationDate: dateString(item.dates?.expiration_date),
    endDate: dateString(item.dates?.end_date),
    travellers: (item.travellers ?? []).map(traveller => ({
      firstName: emptyString(traveller.first_name),
      lastName: emptyString(traveller.last_name),
      title: emptyString(traveller.title),
      roomType: emptyString(traveller.room_type),
      roomNumber: Number.isInteger(traveller.room_number) ? traveller.room_number ?? undefined : undefined,
      isPrimaryContact: traveller.is_primary_contact === true,
    })).filter(traveller => traveller.firstName || traveller.lastName),
    roomAllocations: (item.room_allocations ?? []).map(allocation => ({
      name: emptyString(allocation.name),
      roomIndex: allocation.room_index,
      cancellation: allocation.cancellation?.deadline ? {
        deadline: dateString(allocation.cancellation.deadline),
        timezone: emptyString(allocation.cancellation.timezone),
        type: allocation.cancellation.type,
      } : undefined,
      travellers: allocation.travellers.map(traveller => ({
        firstName: emptyString(traveller.first_name),
        lastName: emptyString(traveller.last_name),
      })).filter(traveller => traveller.firstName || traveller.lastName),
    })),
    itinerary: (item.itinerary ?? []).map(entry => ({
      day: entry.day,
      title: emptyString(entry.title),
      details: entry.details.map(emptyString).filter((detail): detail is string => Boolean(detail)),
    })),
    airlineLogoUrl: emptyString(item.airline_logo_url),
    tripType: item.trip_type === 'return' || item.trip_type === 'one_way' ? item.trip_type : undefined,
    ...(item.property ? {
      property: {
        id: emptyString(item.property.id),
        name: emptyString(item.property.name),
      },
    } : {}),
  }
}

function mapContact(contact: AgentHubTravellerApiContact | undefined): App.AgentHubTravellerContact {
  if (!contact) {
    return {}
  }

  const name = emptyString(contact.name)
  const agencyName = emptyString(contact.agency_name)
  const email = emptyString(contact.email)
  const guidance = emptyString(contact.guidance)

  if (name || agencyName || email) {
    return {
      name,
      agencyName,
      email,
      ...(guidance ? { guidance } : {}),
    }
  }

  return guidance ? { guidance } : {}
}

export function agentHubTravellerOrderMap(
  order: AgentHubTravellerApiOrder,
  type: 'summary' | 'full' = 'summary',
): App.AgentHubTravellerOrder {
  return {
    responseType: AGENT_HUB_TRAVELLER_RESPONSE_TYPE,
    id: order.id,
    bookingNumber: order.number,
    brand: order.brand,
    status: order.status as App.OrderStatus,
    displayStatus: order.display_status,
    createdAt: order.created_at,
    updatedAt: order.updated_at,
    items: (order.items ?? []).map(mapItem),
    agentContact: mapContact(order.agent_contact),
    type,
    isBookingOwner: false,
    accessDetails: {
      role: 'AGENT_BOOKING_TRAVELLER',
      isSharedUser: true,
      canView: true,
      canEdit: false,
      canPayShare: false,
    },
  }
}
